<template>
  <div>
    <!-- Botão flutuante -->
    <div 
      class="chat-floating-button"
      @click="openChat"
      :class="{ 'shake': hasUnreadMessages }"
    >
      <i class="fas fa-comments"></i>
      <span v-if="hasUnreadMessages" class="notification-badge">!</span>
    </div>

    <!-- Janela do chat -->
    <div v-if="isOpen" class="chat-window">
      <!-- Header -->
      <div class="chat-header">
        <h5 class="chat-title">
          <i class="fas fa-robot"></i>
          Assistente Virtual
        </h5>
        <div class="chat-header-actions">
          <button @click="startNewChat" class="chat-new-btn" title="Nova conversa">
            <i class="fas fa-plus"></i>
          </button>
          <button @click="closeChat" class="chat-close-btn" title="Fechar">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Corpo do chat -->
      <div class="chat-body" ref="chatBody">
        <div v-if="messages.length === 0" class="welcome-message">
          <p>👋 <strong>Olá! Sou seu assistente virtual.</strong></p>
          <p>Estou aqui para te ajudar a usar o Preço Público!</p>
          <p>📝 Pode me perguntar como:</p>
          <ul style="text-align: left; margin: 10px 0;">
            <li>🔍 Pesquisar produtos</li>
            <li>📊 Criar relatórios</li>
            <li>💼 Solicitar cotações</li>
            <li>🧭 Navegar no sistema</li>
          </ul>
          <p><em>Digite sua pergunta abaixo!</em></p>
        </div>

        <div 
          v-for="message in messages" 
          :key="message.id"
          class="message"
          :class="{ 'message-user': message.type === 'user', 'message-bot': message.type === 'bot' }"
        >
          <div class="message-content">
            <div class="message-text" v-if="message.type === 'user'">{{ message.text }}</div>
            <div class="message-text" v-else v-html="renderMarkdown(message.text)"></div>
            <div class="message-time">{{ formatTime(message.created_at) }}</div>
          </div>
        </div>

        <div v-if="isTyping" class="typing-indicator">
          <div class="typing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <small>Digitando...</small>
        </div>
      </div>

      <!-- Input -->
      <div class="chat-input">
        <div class="input-group">
          <input 
            v-model="newMessage" 
            @keyup.enter="sendMessage"
            @input="handleTyping"
            type="text" 
            class="form-control" 
            placeholder="Ex: Como pesquisar produtos?"
            :disabled="isTyping"
            maxlength="1000"
          >
          <button 
            @click="sendMessage" 
            class="btn btn-primary"
            :disabled="!newMessage.trim() || isTyping"
          >
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>
        <small class="text-muted">{{ newMessage.length }}/1000</small>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SupportChat',
  data() {
    return {
      isOpen: false,
      messages: [],
      newMessage: '',
      isTyping: false,
      hasUnreadMessages: false,
      currentSessionId: null,
      currentQuestionId: null,
      pollingInterval: null
    }
  },
  mounted() {
    console.log('SupportChat component mounted!')
    this.setupAxios()
    this.loadChatHistory()
    this.restoreChatState()
    this.startPolling()
  },
  beforeUnmount() {
    this.stopPolling()
    this.saveChatState()
  },
  methods: {
    setupAxios() {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (token) {
        axios.defaults.headers.common['X-CSRF-Token'] = token
        axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
      }
    },
    openChat() {
      this.isOpen = true
      this.hasUnreadMessages = false
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    closeChat() {
      this.isOpen = false
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return

      const userMessage = {
        id: Date.now(),
        text: this.newMessage,
        type: 'user',
        created_at: new Date()
      }

      this.messages.push(userMessage)
      const questionText = this.newMessage
      this.newMessage = ''
      this.isTyping = true

      this.$nextTick(() => {
        this.scrollToBottom()
      })

      try {
        const requestData = {
          question: questionText
        }
        
        if (this.currentSessionId) {
          requestData.session_id = this.currentSessionId
        }

        const response = await axios.post('/api/support/questions', requestData)

        this.currentSessionId = response.data.session_id
        this.currentQuestionId = response.data.question_id

        // Simular delay de digitação
        setTimeout(async () => {
          const botMessage = {
            id: Date.now() + 1,
            text: response.data.answer,
            type: 'bot',
            created_at: new Date()
          }

          this.messages.push(botMessage)
          this.isTyping = false
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }, 1500)

      } catch (error) {
        console.error('Error sending message:', error)
        this.isTyping = false
        
        const errorMessage = {
          id: Date.now() + 1,
          text: 'Desculpe, ocorreu um erro. Tente novamente.',
          type: 'bot',
          created_at: new Date()
        }
        this.messages.push(errorMessage)
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },

    async startNewChat() {
      this.messages = []
      this.currentSessionId = null
      this.currentQuestionId = null
      this.isTyping = false
      
      try {
        await axios.post('/api/support/new_session')
      } catch (error) {
        console.error('Error starting new session:', error)
      }
    },
    async loadChatHistory() {
      try {
        const sessionsResponse = await axios.get('/api/support/sessions')
        
        if (sessionsResponse.data.length > 0) {
          // Carregar a sessão mais recente
          const latestSession = sessionsResponse.data[0]
          this.currentSessionId = latestSession.id
          
          const messagesResponse = await axios.get(`/api/support/sessions/${latestSession.id}/messages`)
          
          this.messages = messagesResponse.data.flatMap(question => {
            const messages = [{
              id: `q-${question.id}`,
              text: question.question,
              type: 'user',
              created_at: new Date(question.created_at)
            }]
            
            if (question.latest_answer) {
              messages.push({
                id: `a-${question.latest_answer.id}`,
                text: question.latest_answer.answer,
                type: 'bot',
                created_at: new Date(question.latest_answer.created_at)
              })
            }
            
            return messages
          })
          
          this.messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        }
      } catch (error) {
        console.error('Error loading chat history:', error)
      }
    },
    scrollToBottom() {
      // Múltiplas tentativas para garantir o scroll
      const attemptScroll = () => {
        const chatBody = this.$refs.chatBody
        if (chatBody) {
          chatBody.scrollTop = chatBody.scrollHeight
        }
      }
      
      this.$nextTick(() => {
        attemptScroll()
        
        // Tenta novamente após um pequeno delay
        setTimeout(() => {
          attemptScroll()
        }, 100)
        
        // Tenta uma última vez com delay maior
        setTimeout(() => {
          attemptScroll()
        }, 500)
      })
    },
    formatTime(date) {
      return new Date(date).toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },
    handleTyping() {
      // Implementar feedback visual de digitação se necessário
    },
    renderMarkdown(text) {
      // Renderizar links markdown simples [texto](url)
      return text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="chat-link">$1</a>')
                .replace(/\n/g, '<br>')
    },
    saveChatState() {
      const state = {
        isOpen: this.isOpen
      }
      localStorage.setItem('supportChatState', JSON.stringify(state))
    },
    restoreChatState() {
      const saved = localStorage.getItem('supportChatState')
      if (saved) {
        try {
          const state = JSON.parse(saved)
          this.isOpen = state.isOpen
          if (this.isOpen) {
            this.$nextTick(() => {
              this.scrollToBottom()
            })
          }
        } catch (e) {
          console.error('Erro ao restaurar estado do chat:', e)
        }
      }
    },
    startPolling() {
      // Fazer polling a cada 30 segundos quando o chat estiver aberto
      this.pollingInterval = setInterval(() => {
        if (this.isOpen && this.currentSessionId) {
          this.checkForUpdates()
        }
      }, 30000)
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },
    async checkForUpdates() {
      try {
        const response = await axios.get(`/api/support/sessions/${this.currentSessionId}/messages`)
        const serverMessages = response.data.flatMap(question => {
          const messages = [{
            id: `q-${question.id}`,
            text: question.question,
            type: 'user',
            created_at: new Date(question.created_at)
          }]
          
          if (question.latest_answer) {
            messages.push({
              id: `a-${question.latest_answer.id}`,
              text: question.latest_answer.answer,
              type: 'bot',
              created_at: new Date(question.latest_answer.created_at)
            })
          }
          
          return messages
        })

        serverMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        
        // Verificar se há novas mensagens
        if (serverMessages.length > this.messages.length) {
          this.messages = serverMessages
          this.$nextTick(() => {
            this.scrollToBottom()
          })
          if (!this.isOpen) {
            this.hasUnreadMessages = true
          }
        }
      } catch (error) {
        console.error('Erro ao verificar atualizações:', error)
      }
    }
  },
  watch: {
    isOpen(newVal) {
      this.saveChatState()
      if (newVal) {
        this.hasUnreadMessages = false
      }
    }
  }
}
</script>

